import { Add, Delete, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { IBooking, OrderItem } from '../../utils/entities/interface';
import { get, post } from '../../services/https';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../components/atoms/Button';
import useSnackbar from '../../hooks/useSnackbar';
import { capitalizeWords, formatDate } from '../../utils/helper';
import {
  calculateInvoiceTotals,
  resolveGstRate,
} from '../../utils/invoiceGst';
import {
  AssignedUserCoupon,
  getUserCoupons,
} from '../../services/userCoupons';

type InvoiceType = 'STANDARD' | 'PROMOTIONAL' | 'WARRANTY';

// Build default invoice line items from the user-selected services on a booking,
// so a freshly created invoice is pre-filled instead of empty. Each service (and
// any nested sub-services) becomes an editable order item.
const buildDefaultItemsFromServices = (booking: IBooking | null): OrderItem[] => {
  const details: any[] = Array.isArray(booking?.serviceDetails)
    ? (booking?.serviceDetails as any[])
    : [];
  const items: OrderItem[] = [];

  details.forEach((d) => {
    const subs: any[] = Array.isArray(d?.services) ? d.services : [];
    if (subs.length > 0) {
      subs.forEach((s) => {
        items.push({
          item:
            s?.name ||
            s?.serviceType ||
            d?.service_data?.name ||
            d?.serviceType ||
            'Service',
          quantity: String(s?.quantity ?? d?.quantity ?? 1),
          price: Number(s?.unitPrice ?? s?.price ?? d?.service_data?.unitPrice ?? 0),
          _id: '',
        });
      });
    } else {
      items.push({
        item:
          d?.service_data?.name ||
          d?.serviceType ||
          d?.otherService ||
          'Service',
        quantity: String(d?.quantity ?? 1),
        price: Number(d?.service_data?.unitPrice ?? d?.price ?? 0),
        _id: '',
      });
    }
  });

  return items;
};

interface AddOrderItemsProps {
  open: boolean;
  onClose: () => void;
  orderItems: OrderItem[];
  onReload: () => void;
  booking: IBooking | null;
}

export const AddOrderItems = ({
  open,
  onClose,
  orderItems,
  onReload,
  booking,
}: AddOrderItemsProps) => {
  const { SnackbarComponent, showSnackbar, resetSnackbar } = useSnackbar();
  const [items, setItems] = useState<OrderItem[]>([]);
  const [originalItems, setOriginalItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [applyGst, setApplyGst] = useState(true);
  const [originalApplyGst, setOriginalApplyGst] = useState(true);
  const [activeGstRate, setActiveGstRate] = useState(0);
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('STANDARD');
  const [originalInvoiceType, setOriginalInvoiceType] =
    useState<InvoiceType>('STANDARD');
  const [coupons, setCoupons] = useState<AssignedUserCoupon[]>([]);
  const [selectedUserCouponId, setSelectedUserCouponId] = useState('');
  const [originalUserCouponId, setOriginalUserCouponId] = useState('');
  const [couponApplyAmount, setCouponApplyAmount] = useState<number | ''>('');
  const [originalCouponApplyAmount, setOriginalCouponApplyAmount] = useState<
    number | ''
  >('');

  useEffect(() => {
    // Existing invoice items → load them as-is.
    if (Array.isArray(orderItems) && orderItems.length > 0) {
      setItems(orderItems.map((item) => ({ ...item })));
      setOriginalItems(orderItems.map((item) => ({ ...item })));
      setIsDirty(false);
      return;
    }

    // No saved items yet → seed defaults from the user-selected services so the
    // invoice is pre-populated and can be managed/edited before saving.
    const seeded = buildDefaultItemsFromServices(booking);
    setItems(seeded);
    setOriginalItems([]);
    setIsDirty(seeded.length > 0);
  }, [orderItems, booking]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const initialApplyGst = booking?.applyGst !== false;
    setApplyGst(initialApplyGst);
    setOriginalApplyGst(initialApplyGst);
    const initialInvoiceType = (booking?.invoiceType || 'STANDARD') as InvoiceType;
    setInvoiceType(initialInvoiceType);
    setOriginalInvoiceType(initialInvoiceType);
    const initialUserCouponId =
      initialInvoiceType === 'STANDARD'
        ? booking?.appliedCoupon?.userCouponId || ''
        : '';
    const initialCouponAmount =
      initialInvoiceType === 'STANDARD' && booking?.discountAmount
        ? Number(booking.discountAmount)
        : '';
    setSelectedUserCouponId(initialUserCouponId);
    setOriginalUserCouponId(initialUserCouponId);
    setCouponApplyAmount(initialCouponAmount);
    setOriginalCouponApplyAmount(initialCouponAmount);

    const fetchActiveTax = async () => {
      try {
        const response = await get('/api/v1/admin/tax-configs/active');
        setActiveGstRate(Number(response.data?.data?.gst || 0));
      } catch {
        setActiveGstRate(Number(booking?.tax?.gst || 0));
      }
    };

    fetchActiveTax();

    const fetchCoupons = async () => {
      try {
        const userId = booking?.user?._id || booking?.user_id;
        if (!userId) {
          setCoupons([]);
          return;
        }

        const response = await getUserCoupons(userId);
        setCoupons(
          (response.data || []).filter(
            (coupon) =>
              coupon._id === initialUserCouponId ||
              (coupon.status === 'ACTIVE' &&
                Number(coupon.remainingAmount || 0) > 0 &&
                coupon.coupon?.isActive !== false),
          ),
        );
      } catch {
        setCoupons([]);
      }
    };

    fetchCoupons();
  }, [open, booking]);

  const gstRate = useMemo(
    () => resolveGstRate(booking, applyGst, activeGstRate),
    [booking, applyGst, activeGstRate],
  );

  const invoiceTotals = useMemo(
    () => calculateInvoiceTotals(items, gstRate),
    [items, gstRate],
  );

  const selectedCoupon = useMemo(
    () =>
      coupons.find((coupon) => coupon._id === selectedUserCouponId) ||
      null,
    [coupons, selectedUserCouponId],
  );

  const selectedCouponBalance = useMemo(() => {
    if (!selectedCoupon) return 0;
    const currentRemaining = Number(selectedCoupon.remainingAmount || 0);
    const editingSameCoupon =
      selectedCoupon._id === originalUserCouponId
        ? Number(originalCouponApplyAmount || 0)
        : 0;
    return currentRemaining + editingSameCoupon;
  }, [originalCouponApplyAmount, originalUserCouponId, selectedCoupon]);

  const maxCouponAmount = useMemo(() => {
    if (!selectedCoupon || invoiceType !== 'STANDARD') return 0;
    return Math.min(
      invoiceTotals.grandTotal,
      (invoiceTotals.grandTotal *
        Number(selectedCoupon.coupon?.discount || 0)) /
        100,
      selectedCouponBalance,
    );
  }, [invoiceTotals.grandTotal, invoiceType, selectedCoupon, selectedCouponBalance]);

  const effectiveDiscountAmount = useMemo(() => {
    if (invoiceType !== 'STANDARD') {
      return invoiceTotals.grandTotal;
    }

    if (!selectedCoupon) return 0;

    const requested = Number(couponApplyAmount || 0);
    return Math.min(Math.max(0, requested), maxCouponAmount);
  }, [couponApplyAmount, invoiceTotals.grandTotal, invoiceType, maxCouponAmount, selectedCoupon]);

  const finalInvoiceTotal = Math.max(
    0,
    invoiceTotals.grandTotal - effectiveDiscountAmount,
  );

  const handleItemChange = <K extends keyof OrderItem>(
    index: number,
    field: K,
    value: OrderItem[K],
  ) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    checkIfDirty(newItems);
  };

  const handleAddItem = () => {
    const newItem = { item: '', quantity: '1', price: 0, _id: '' };
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    checkIfDirty(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    checkIfDirty(updatedItems);
  };

  const validateItems = (): string | null => {
    for (let index = 0; index < items.length; index += 1) {
      const row = items[index];
      const description = String(row.item || '').trim();
      const quantity = Number(row.quantity);
      const price = Number(row.price);

      if (description === '') {
        return `Row ${index + 1}: description is required`;
      }
      if (
        row.quantity === '' ||
        !Number.isInteger(quantity) ||
        quantity < 1
      ) {
        return `Row ${index + 1}: quantity must be at least 1`;
      }
      if (String(row.price) === '' || !Number.isFinite(price) || price < 0) {
        return `Row ${index + 1}: a valid price is required`;
      }
    }
    return null;
  };

  const handleSaveItems = async () => {
    const validationError = validateItems();
    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    setLoading(true);
    try {
      const bookingId = booking ? booking._id : '';
      if (!bookingId) {
        showSnackbar('Booking not found', 'error');
        return;
      }

      const response = await post('/api/v1/admin/booking/order-items/add-edit', {
        bookingId,
        isUpdating: 1,
        orderItem: items,
        applyGst,
        invoiceType,
        userCouponId: invoiceType === 'STANDARD' ? selectedUserCouponId : '',
        couponApplyAmount:
          invoiceType === 'STANDARD' ? Number(couponApplyAmount || 0) : 0,
      });

      if (response.status === 200) {
        showSnackbar('Items saved successfully', 'success');
        await createInvoice(bookingId);
        onReload();
        setOriginalItems(items);
        setOriginalApplyGst(applyGst);
        setIsDirty(false);
        onClose();
        resetSnackbar();
      } else {
        showSnackbar(
          response.data?.message || 'Failed to save the items',
          'error',
        );
      }
    } catch (error) {
      console.error('Error saving order items:', error);
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to save order items',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  async function createInvoice(bookingId: string) {
    try {
      const response = await get(
        `/api/v1/admin/booking/generate-invoice/${bookingId}`,
      );
      const data = response.data;

      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
        showSnackbar('Invoice generated successfully', 'success');
      } else {
        showSnackbar(
          data?.message || 'Invoice created but no download URL returned',
          'error',
        );
      }
    } catch (error) {
      console.error('Invoice generation failed:', error);
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to generate invoice',
        'error',
      );
    }
  }

  const handleResetItems = () => {
    setItems(originalItems);
    setApplyGst(originalApplyGst);
    setInvoiceType(originalInvoiceType);
    setSelectedUserCouponId(originalUserCouponId);
    setCouponApplyAmount(originalCouponApplyAmount);
    setIsDirty(false);
  };

  const checkIfDirty = (
    newItems: OrderItem[],
    nextApplyGst = applyGst,
  ) => {
    if (
      nextApplyGst !== originalApplyGst ||
      invoiceType !== originalInvoiceType ||
      selectedUserCouponId !== originalUserCouponId ||
      couponApplyAmount !== originalCouponApplyAmount
    ) {
      setIsDirty(true);
      return;
    }

    if (newItems.length !== originalItems.length) {
      setIsDirty(true);
      return;
    }

    const isChanged = newItems.some((item, index) => {
      return (
        item.item !== originalItems[index]?.item ||
        item.quantity !== originalItems[index]?.quantity ||
        item.price !== originalItems[index]?.price
      );
    });
    setIsDirty(isChanged);
  };

  const handleApplyGstChange = (checked: boolean) => {
    setApplyGst(checked);
    checkIfDirty(items, checked);
  };

  const handleInvoiceTypeChange = (nextType: InvoiceType) => {
    setInvoiceType(nextType);
    if (nextType !== 'STANDARD') {
      setApplyGst(false);
      setSelectedUserCouponId('');
      setCouponApplyAmount('');
    } else {
      setApplyGst(originalApplyGst);
    }
    setIsDirty(true);
  };

  const handleCouponChange = (userCouponId: string) => {
    if (!userCouponId) {
      setSelectedUserCouponId('');
      setCouponApplyAmount('');
      setIsDirty(true);
      return;
    }

    const coupon = coupons.find((item) => item._id === userCouponId);
    if (coupon) {
      const minValue = Number(coupon.coupon?.minValue || 0);
      if (minValue > 0 && invoiceTotals.grandTotal < minValue) {
        showSnackbar(
          `This coupon requires a minimum invoice value of £${minValue.toFixed(2)}`,
          'error',
        );
        return;
      }

      const maxAmount = Math.min(
        invoiceTotals.grandTotal,
        (invoiceTotals.grandTotal *
          Number(coupon.coupon?.discount || 0)) /
          100,
        Number(coupon.remainingAmount || 0),
      );
      setSelectedUserCouponId(userCouponId);
      setCouponApplyAmount(parseFloat(maxAmount.toFixed(2)));
    } else {
      setSelectedUserCouponId(userCouponId);
    }
    setIsDirty(true);
  };

  const isEditingInvoice = Boolean(booking?.invoiceCreated || booking?.invoiceUrl);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'auto', // Ensures the background doesn't leak
        },
      }}
    >
      {' '}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: 10,
          overflow: 'auto', // Prevent border from affecting layout
        }}
      >
        {SnackbarComponent}
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#181818',
            padding: '16px',
          }}
        >
          {isEditingInvoice ? 'Edit Invoice' : 'Create Invoice'}
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{
            backgroundColor: '#181818',
            padding: '24px',
            maxHeight: '70vh', // Limit total content height
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              // paddingBottom: 1,
              // marginBottom: 2,
              // padding: 1,
              // my: 5,
              marginBottom: 4,
            }}
          >
            <Box>
              <Typography variant="h6" color="primary.light">
                Bill To:
              </Typography>
              <Typography sx={{ color: '#FFFFFF' }}>
                {booking ? capitalizeWords(booking.user.name) : 'N/A'}
              </Typography>
              <Typography sx={{ color: '#FFFFFF' }}>
                {booking
                  ? 'Phone:' + ' ' + '+91' + booking?.user.phoneNumber
                  : 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" color="primary.light">
                Invoice Details
              </Typography>
              <Typography sx={{ color: '#FFFFFF' }}>
                Invoice ID: {booking ? booking.bookingId : ''}
              </Typography>
              <Typography sx={{ color: '#FFFFFF' }}>
                Date: {booking ? formatDate(booking.date) : 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mb: 3,
              p: 2,
              border: '1px solid #333',
              borderRadius: 2,
              backgroundColor: '#1f1f1f',
            }}
          >
            <TextField
              select
              label="Invoice Type"
              value={invoiceType}
              onChange={(e) =>
                handleInvoiceTypeChange(e.target.value as InvoiceType)
              }
              disabled={loading}
              sx={{ backgroundColor: '#222', borderRadius: '8px' }}
            >
              <MenuItem value="STANDARD">Standard</MenuItem>
              <MenuItem value="PROMOTIONAL">Promotional (£0)</MenuItem>
              <MenuItem value="WARRANTY">Warranty (£0)</MenuItem>
            </TextField>

            {invoiceType === 'STANDARD' ? (
              <TextField
                select
                label="Apply Coupon"
                value={selectedUserCouponId}
                onChange={(e) => handleCouponChange(e.target.value)}
                disabled={loading}
                sx={{ backgroundColor: '#222', borderRadius: '8px' }}
              >
                <MenuItem value="">No Coupon</MenuItem>
                {coupons.map((coupon) => (
                  <MenuItem key={coupon._id} value={coupon._id}>
                    {coupon.couponCode} - {coupon.coupon?.discount || 0}% off
                    (£{Number(coupon.remainingAmount || 0).toFixed(2)} left)
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography sx={{ color: 'success.main', alignSelf: 'center' }}>
                {invoiceType === 'PROMOTIONAL'
                  ? 'Promotional invoice will make the payable amount £0.'
                  : 'Warranty invoice will make the payable amount £0.'}
              </Typography>
            )}

            {invoiceType === 'STANDARD' && selectedCoupon ? (
              <TextField
                label={`Coupon Amount (max £${maxCouponAmount.toFixed(2)})`}
                type="number"
                onWheel={(e) => (e.target as HTMLElement).blur()}
                value={couponApplyAmount}
                onChange={(e) => {
                  const value =
                    e.target.value === '' ? '' : Number(e.target.value);
                  setCouponApplyAmount(value);
                  setIsDirty(true);
                }}
                inputProps={{ min: 0, max: maxCouponAmount, step: 0.01 }}
                disabled={loading}
                helperText={`Minimum invoice value £${selectedCoupon.coupon?.minValue || 0}. Remaining balance £${Number(selectedCoupon.remainingAmount || 0).toFixed(2)}`}
                sx={{ backgroundColor: '#222', borderRadius: '8px' }}
              />
            ) : null}
          </Box>
          <Box
            sx={{
              maxHeight: '300px', // Adjust height threshold as needed
              overflowY: 'auto',
              pr: 1, // Optional padding-right for scrollbar space
            }}
          >
            {items.map((item, index) => (
              <Grid
                key={index}
                container
                spacing={2}
                alignItems="center"
                sx={{ marginBottom: 1 }}
              >
                <Grid item xs={4}>
                  <Typography gutterBottom color="#8A8D91">
                    Description
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={item.item}
                    onChange={(e) =>
                      handleItemChange(index, 'item', e.target.value)
                    }
                    sx={{
                      backgroundColor: '#222',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Typography gutterBottom color="#8A8D91">
                    Quantity
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    value={item.quantity}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Remove leading zero
                      if (value.length > 1 && value.startsWith('0')) {
                        value = value.replace(/^0+/, '');
                      }

                      handleItemChange(index, 'quantity', value);
                    }}
                    inputProps={{ min: 0 }}
                    sx={{
                      backgroundColor: '#222',
                      borderRadius: '8px',
                      color: '#fff',

                      // 🔥 Hide arrows
                      '& input[type=number]': {
                        MozAppearance: 'textfield', // Firefox
                      },
                      '& input[type=number]::-webkit-outer-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography gutterBottom color="#8A8D91">
                    Unit Price
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    value={item.price}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Remove leading zero
                      if (value.length > 1 && value.startsWith('0')) {
                        value = value.replace(/^0+/, '');
                      }
                      handleItemChange(index, 'price', value);
                    }}
                    inputProps={{ min: 0 }}
                    sx={{
                      backgroundColor: '#222',
                      borderRadius: '8px',
                      color: '#fff',

                      // 🔥 Hide arrows
                      '& input[type=number]': {
                        MozAppearance: 'textfield', // Firefox
                      },
                      '& input[type=number]::-webkit-outer-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '& input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Typography gutterBottom color="#8A8D91">
                    Total Price
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    value={Number(item.price) * Number(item.quantity)}
                    sx={{
                      backgroundColor: '#222',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={1} sx={{ textAlign: 'center' }}>
                  <IconButton
                    onClick={() => handleRemoveItem(index)}
                    sx={{ color: 'error.main', mt: 4 }}
                    disabled={loading || items.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={handleAddItem}
              sx={{ borderRadius: '8px' }}
            >
              Add More
            </Button>
          </Box>

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: '1px solid #333',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={applyGst}
                  onChange={(e) => handleApplyGstChange(e.target.checked)}
                  disabled={loading || activeGstRate <= 0}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#7B1FA2',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#7B1FA2',
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: '#fff' }}>
                  Apply VAT{activeGstRate > 0 ? ` (${activeGstRate}%)` : ''}
                </Typography>
              }
            />

            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ color: '#8A8D91' }}>
                Subtotal: £{invoiceTotals.subtotal.toFixed(2)}
              </Typography>
              {applyGst && gstRate > 0 ? (
                <Typography sx={{ color: '#8A8D91' }}>
                  VAT ({gstRate}%): £{invoiceTotals.gstAmount.toFixed(2)}
                </Typography>
              ) : null}
              {effectiveDiscountAmount > 0 ? (
                <Typography sx={{ color: 'success.main' }}>
                  Discount: -£{effectiveDiscountAmount.toFixed(2)}
                </Typography>
              ) : null}
              <Typography sx={{ color: '#fff', fontWeight: 700, mt: 0.5 }}>
                Grand Total: £{finalInvoiceTotal.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            backgroundColor: '#181818',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 5,
          }}
        >
          <Box>
            {/* <IconPrimaryButton
              title="Add More"
              onClick={handleAddItem}
              icon={<Add />}
            /> */}
          </Box>
          <Box display={'flex'} gap={2}>
            <SecondaryButton
              title="Reset"
              onClick={handleResetItems}
              disabled={!isDirty || loading}
            />

            <PrimaryButton
              title="Save"
              onClick={handleSaveItems}
              disabled={!isDirty || loading}
            />
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
